/**
 * Сбор сигналов честности набора.
 *
 * Клиент только собирает наблюдения; решение принимает сервер (ТЗ FR-VT-04).
 * Никакой сигнал сам по себе не является доказательством: пользователь может
 * переключиться в другое окно по бытовой причине.
 */

import type { KeystrokeEvent } from '@type2x/shared-contracts';

/** Наблюдения за одним контрольным прогоном. */
export interface IntegritySignals {
  /** Была ли попытка вставки из буфера обмена. */
  readonly pasteDetected: boolean;
  /** Встречались ли программно сгенерированные события. */
  readonly syntheticEvents: boolean;
  /** Сколько раз окно теряло фокус. */
  readonly focusLossCount: number;
  /** Коэффициент вариации межклавишных интервалов. */
  readonly intervalVariation: number;
  /** Минимальный зафиксированный интервал, мс. */
  readonly minIntervalMs: number;
  /** Доля интервалов, отличающихся от медианы менее чем на 1 мс. */
  readonly identicalIntervalRatio: number;
}

/** Пустые сигналы: набор начался, наблюдений ещё нет. */
export const NO_SIGNALS: IntegritySignals = Object.freeze({
  pasteDetected: false,
  syntheticEvents: false,
  focusLossCount: 0,
  intervalVariation: 0,
  minIntervalMs: 0,
  identicalIntervalRatio: 0,
});

/** Насколько интервалы считаются неразличимыми, мс. */
const IDENTICAL_TOLERANCE_MS = 1;

function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/** Считает статистику интервалов, по которой видно машинный ритм. */
export function analyseIntervals(
  intervals: readonly number[],
): Pick<IntegritySignals, 'intervalVariation' | 'minIntervalMs' | 'identicalIntervalRatio'> {
  const usable = intervals.filter((value) => value > 0);
  if (usable.length < 2) {
    return { intervalVariation: 0, minIntervalMs: 0, identicalIntervalRatio: 0 };
  }

  const average = mean(usable);
  const variance = mean(usable.map((value) => (value - average) ** 2));
  const identical = usable.filter(
    (value) => Math.abs(value - average) < IDENTICAL_TOLERANCE_MS,
  ).length;

  return {
    intervalVariation: average === 0 ? 0 : Math.sqrt(variance) / average,
    minIntervalMs: Math.min(...usable),
    identicalIntervalRatio: identical / usable.length,
  };
}

/** Наблюдатель сигналов на время одного контрольного прогона. */
export class IntegrityRecorder {
  private paste = false;
  private synthetic = false;
  private focusLoss = 0;

  /** Отмечает попытку вставки из буфера обмена. */
  recordPaste(): void {
    this.paste = true;
  }

  /** Отмечает программно сгенерированное событие. */
  recordUntrustedEvent(): void {
    this.synthetic = true;
  }

  /** Отмечает потерю фокуса окном. */
  recordFocusLoss(): void {
    this.focusLoss += 1;
  }

  /** Собирает итоговые сигналы прогона. */
  collect(events: readonly KeystrokeEvent[]): IntegritySignals {
    return {
      pasteDetected: this.paste,
      syntheticEvents: this.synthetic,
      focusLossCount: this.focusLoss,
      ...analyseIntervals(events.map((event) => event.interKeyIntervalMs)),
    };
  }
}

/** Подключает наблюдателя к документу и возвращает функцию отписки. */
export function attachIntegrityListeners(recorder: IntegrityRecorder): () => void {
  const onPaste = (): void => recorder.recordPaste();
  const onBlur = (): void => recorder.recordFocusLoss();

  globalThis.document.addEventListener('paste', onPaste);
  globalThis.addEventListener('blur', onBlur);

  return () => {
    globalThis.document.removeEventListener('paste', onPaste);
    globalThis.removeEventListener('blur', onBlur);
  };
}
